import { createContext, lazy, Suspense, useCallback, useContext, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

const LazyNewOrderDialog = lazy(() =>
  import("./NewOrderDialog").then((module) => ({ default: module.NewOrderDialog })),
);

type NewOrderDialogContextValue = {
  isNewOrderOpen: boolean;
  openNewOrder: () => void;
  closeNewOrder: () => void;
};

const NewOrderDialogContext = createContext<NewOrderDialogContextValue | null>(null);

export function NewOrderDialogProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [isNewOrderOpen, setIsNewOrderOpen] = useState(false);

  const handleCreated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["orders"] });
    queryClient.invalidateQueries({ queryKey: ["quotes"] });
  }, [queryClient]);

  return (
    <NewOrderDialogContext.Provider
      value={{
        isNewOrderOpen,
        openNewOrder: () => setIsNewOrderOpen(true),
        closeNewOrder: () => setIsNewOrderOpen(false),
      }}
    >
      {children}
      <Suspense fallback={null}>
        {isNewOrderOpen && (
          <LazyNewOrderDialog
            open={isNewOrderOpen}
            onOpenChange={setIsNewOrderOpen}
            onCreated={handleCreated}
          />
        )}
      </Suspense>
    </NewOrderDialogContext.Provider>
  );
}

export function useNewOrderDialog() {
  const context = useContext(NewOrderDialogContext);

  if (!context) {
    throw new Error("useNewOrderDialog must be used inside NewOrderDialogProvider");
  }

  return context;
}